import { ApolloError } from '@apollo/client/core';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { Company, CompanyPublic } from '@macs-frontend/web-macs/shared';
import { Action } from '@ngrx/store';
import { APIResponse, MdNotification, Project, Timezone, User } from '../';
import { Currency } from '../models/currency.model';
import { GeoCity, GeoCountry } from '../models/geo';
import { Role } from '../models/role';

export enum Pages {
  COMPANIES = 'companies',
  USERS = 'users',
  ACTIVITY_FEED = 'activity feed',
  MESSAGES = 'messages',
  MANAGE = 'manage',
  CLIENT_DETAILS = 'client details',
  CONTACT_DETAILS = 'contact details',
  PROJECTS = 'projects',
  FINANCIAL = 'financial',
  MATERIALS = 'materials',
  PLANNER = 'planner',
  AGENDA = 'agenda',
  EXTERNAL = 'external',
  ADMINISTRATION = 'administration',
  INTERNAL = 'internal',
  TIME = 'time',
}

export enum MetaActionTypes {
  ShowNotification = '[Meta] ShowNotification',
  SetActivePage = '[Meta] SetActivePage',
  ToggleLeftSidebar = '[Meta] ToggleLeftSidebar',
  LoadCurrencies = '[Meta] LoadCurrencies',
  CurrenciesLoaded = '[Meta] CurrenciesLoaded',
  LoadFail = '[Meta] LoadFail',
  LoadFinancialConsts = '[Meta] LoadFinancialConsts',
  FinancialConstsLoaded = '[Meta] FinancialConstsLoaded',
  LoadConsts = '[Meta] LoadConsts',
  ConstsLoaded = '[Meta] ConstsLoaded',
  LoadCities = '[Meta] LoadCities',
  CitiesLoaded = '[Meta] CitiesLoaded',
  LoadLocationConsts = '[Meta] LoadLocationConsts',
  LocationConstsLoaded = '[Meta] LocationConstsLoaded',
  LoadUser = '[Meta] LoadUser',
  UserLoaded = '[Meta] UserLoaded',
  LoadRoleUnits = '[Meta] LoadRoleUnits',
  RoleUnitsLoaded = '[Meta] RoleUnitsLoaded',
  SaveRoleUnit = '[Meta] SaveRoleUnit',
  RoleUnitUpdated = '[Meta] RoleUnitUpdated',
  LoadCurrentCompany = '[Meta] LoadCurrentCompany',
  CurrentCompanyLoaded = '[Meta] CurrentCompanyLoaded',
  CurrentCompanyPublicLoaded = '[Meta] CurrentCompanyPublicLoaded',
  ChangeCurrentCompany = '[Meta] ChangeCurrentCompany',
  ToggleCompanySwitcher = '[Meta] ToggleCompanySwitcher',
  FilterCurrencies = '[Meta] FilterCurrencies',
  LoadDefaultCurrency = '[Meta] LoadDefaultCurrency',
  DefaultCurrencyLoaded = '[Meta] DefaultCurrencyLoaded',
  LoadCurrentPublicCompany = '[Meta] LoadCurrentPublicCompany',
}

export class ShowNotification implements Action {
  constructor(readonly notification: MdNotification) {}

  readonly type = MetaActionTypes.ShowNotification;
  static fromApollo(err: ApolloError, title = 'Heads Up!'): ShowNotification {
    return new ShowNotification({
      title,
      error: {
        title: err.name,
        items: err.graphQLErrors.map(e => e.message),
      },
    });
  }
}

export class SetActivePage implements Action {
  readonly type = MetaActionTypes.SetActivePage;

  constructor(public payload: Pages) {}
}

export class LoadCurrencies implements Action {
  readonly type = MetaActionTypes.LoadCurrencies;

  constructor() {}
}

export class CurrenciesLoaded implements Action {
  readonly type = MetaActionTypes.CurrenciesLoaded;

  constructor(public payload: APIResponse<Currency[]>) {}
}

export class LoadFail implements Action {
  readonly type = MetaActionTypes.LoadFail;

  constructor(public payload: string) {}
}

export class LoadFinancialConsts implements Action {
  readonly type = MetaActionTypes.LoadFinancialConsts;

  constructor() {}
}

export class FinancialConstsLoaded implements Action {
  readonly type = MetaActionTypes.FinancialConstsLoaded;

  constructor(
    public payload: {
      currencies: APIResponse<Currency[]>;
      timezones: APIResponse<Timezone[]>;
      languages: APIResponse<string[]>;
    },
  ) {}
}

export class LoadConsts implements Action {
  readonly type = MetaActionTypes.LoadConsts;

  constructor() {}
}

export class ConstsLoaded implements Action {
  readonly type = MetaActionTypes.ConstsLoaded;

  constructor(
    public payload: {
      currencies: APIResponse<Currency[]>;
      timezones: APIResponse<Timezone[]>;
      languages: APIResponse<string[]>;
    },
  ) {}
}

export class LoadCities implements Action {
  readonly type = MetaActionTypes.LoadCities;

  constructor(
    public payload: {
      name: string;
      countryUuid?: UUID;
    },
  ) {}
}

export class CitiesLoaded implements Action {
  readonly type = MetaActionTypes.CitiesLoaded;

  constructor(
    public payload: {
      cities: APIResponse<GeoCity[]>;
    },
  ) {}
}

export class LoadLocationConsts implements Action {
  readonly type = MetaActionTypes.LoadLocationConsts;

  constructor() {}
}

export class LocationConstsLoaded implements Action {
  readonly type = MetaActionTypes.LocationConstsLoaded;

  constructor(
    public payload: {
      countries: APIResponse<GeoCountry[]>;
    },
  ) {}
}

export class LoadUser implements Action {
  readonly type = MetaActionTypes.LoadUser;

  constructor() {}
}

export class UserLoaded implements Action {
  readonly type = MetaActionTypes.UserLoaded;

  constructor(public payload: APIResponse<User>) {}
}

export class LoadRoleUnits implements Action {
  readonly type = MetaActionTypes.LoadRoleUnits;

  constructor() {}
}

export class RoleUnitsLoaded implements Action {
  readonly type = MetaActionTypes.RoleUnitsLoaded;

  constructor(public payload: APIResponse<Role[]>) {}
}

export class SaveRoleUnit implements Action {
  readonly type = MetaActionTypes.SaveRoleUnit;

  constructor(public payload: { roleUnitId: UUID; userId: UUID; project: Project }) {}
}

export class RoleUnitUpdated implements Action {
  readonly type = MetaActionTypes.RoleUnitUpdated;

  constructor(public payload: Role) {}
}

export class LoadCurrentCompany implements Action {
  readonly type = MetaActionTypes.LoadCurrentCompany;

  constructor() {}
}

export class CurrentCompanyLoaded implements Action {
  readonly type = MetaActionTypes.CurrentCompanyLoaded;

  constructor(public payload: Company) {}
}

export class CurrentCompanyPublicLoaded implements Action {
  readonly type = MetaActionTypes.CurrentCompanyPublicLoaded;

  constructor(public payload: CompanyPublic) {}
}

export class ChangeCurrentCompany implements Action {
  readonly type = MetaActionTypes.ChangeCurrentCompany;

  constructor(public payload: UUID) {}
}

export class ToggleCompanySwitcher implements Action {
  readonly type = MetaActionTypes.ToggleCompanySwitcher;

  constructor(public payload: boolean) {}
}

export class FilterCurrencies implements Action {
  readonly type = MetaActionTypes.FilterCurrencies;

  constructor(public payload: string) {}
}

export class LoadDefaultCurrency implements Action {
  readonly type = MetaActionTypes.LoadDefaultCurrency;

  constructor(public payload: string) {}
}

export class DefaultCurrencyLoaded implements Action {
  readonly type = MetaActionTypes.DefaultCurrencyLoaded;

  constructor(public payload: Currency) {}
}

export class LoadCurrentPublicCompany implements Action {
  readonly type = MetaActionTypes.LoadCurrentPublicCompany;
}

export type MetaActions =
  | SetActivePage
  | LoadCurrencies
  | CurrenciesLoaded
  | LoadFail
  | LoadFinancialConsts
  | FinancialConstsLoaded
  | LoadConsts
  | ConstsLoaded
  | LoadCities
  | CitiesLoaded
  | LoadLocationConsts
  | LocationConstsLoaded
  | LoadUser
  | UserLoaded
  | LoadRoleUnits
  | RoleUnitsLoaded
  | SaveRoleUnit
  | RoleUnitUpdated
  | LoadCurrentCompany
  | CurrentCompanyLoaded
  | CurrentCompanyPublicLoaded
  | ChangeCurrentCompany
  | ToggleCompanySwitcher
  | LoadDefaultCurrency
  | DefaultCurrencyLoaded
  | LoadCurrentPublicCompany;
